/**
 * Sales Automation Control Section
 * Section 5: Deal acceleration, proposals, high-intent leads
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface SalesAutomationControlProps {
  data?: {
    dealAccelerationTriggers?: number;
    proposalQueue?: number;
    highIntentLeads?: number;
    salesAutopilotMetrics?: {
      sent: number;
      opens: number;
      replies: number;
    };
    founderInvolvementRecommendations?: Array<{
      leadId: string;
      reason: string;
      priority: 'high' | 'medium' | 'low';
    }>;
  };
}

export function SalesAutomationControl({ data }: SalesAutomationControlProps) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Deal Acceleration Triggers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.dealAccelerationTriggers || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Proposal Queue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.proposalQueue || 0}</div>
            <Button variant="outline" size="sm" className="mt-2">
              Review Proposals
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">High-Intent Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.highIntentLeads || 0}</div>
            <Button variant="outline" size="sm" className="mt-2">
              View Leads
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sales Autopilot Metrics</CardTitle>
          <CardDescription>Automated sales sequence performance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Sent</div>
              <div className="text-2xl font-bold">{data?.salesAutopilotMetrics?.sent || 0}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Opens</div>
              <div className="text-2xl font-bold">{data?.salesAutopilotMetrics?.opens || 0}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Replies</div>
              <div className="text-2xl font-bold">{data?.salesAutopilotMetrics?.replies || 0}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {data?.founderInvolvementRecommendations && data.founderInvolvementRecommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Founder Involvement Recommendations</CardTitle>
            <CardDescription>High-value leads requiring founder attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.founderInvolvementRecommendations.map((rec, index) => (
                <div key={index} className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">Lead {rec.leadId.slice(0, 8)}</div>
                    <div className="text-sm text-muted-foreground">{rec.reason}</div>
                  </div>
                  <Badge
                    variant={
                      rec.priority === 'high'
                        ? 'destructive'
                        : rec.priority === 'medium'
                        ? 'default'
                        : 'outline'
                    }
                  >
                    {rec.priority}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
